import { useEffect, useState } from 'react'
import { useForm, useWatch } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import {
  ArrowLeft,
  ArrowRight,
  Download,
  Loader2,
  Phone,
  Upload,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'
import {
  downloadProspectsTemplate,
  parseProspectsCsv,
  prospectDisplayName,
  type ProspectsCsvResult,
} from '@/lib/csvProspects'
import { useTeamMembers } from '@/api/agents'
import {
  useCampaignDetail,
  useCreateCampaign,
  useImportProspects,
  useOwnedPDNumbers,
} from '@/api/powerDialer'
import { CampaignDispositionsSection } from './CampaignDispositionsSection'

// =============================================================================
// Schéma RHF / Zod
// =============================================================================
const campaignSchema = z.object({
  name: z.string().trim().min(1, 'name_required'),
  description: z.string(),
  script: z.string(),
  number_ids: z.array(z.number()),
  agent_ids: z.array(z.number()),
  max_attempts: z.number().int().min(1).max(20),
  daily_cap_per_number: z.number().int().min(1).max(10_000),
  auto_rotation: z.boolean(),
  local_presence_enabled: z.boolean(),
  sticky_enabled: z.boolean(),
})
type FormValues = z.infer<typeof campaignSchema>

const DEFAULTS: FormValues = {
  name: '',
  description: '',
  script: '',
  number_ids: [],
  agent_ids: [],
  max_attempts: 6,
  daily_cap_per_number: 100,
  auto_rotation: true,
  local_presence_enabled: false,
  sticky_enabled: true,
}

const STEPS = ['general', 'settings', 'agents', 'prospects'] as const
type StepKey = (typeof STEPS)[number]

interface Props {
  campaignId?: number
  onBack: () => void
}

export function CampaignFormView({ campaignId, onBack }: Props) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!campaignId

  const [step, setStep] = useState<number>(0)
  const [csvFile, setCsvFile] = useState<File | null>(null)
  // Prévisualisation client des prospects du CSV (miroir du parsing backend).
  const [csvPreview, setCsvPreview] = useState<ProspectsCsvResult | null>(null)
  const [importResult, setImportResult] = useState<{
    imported: number
    skipped: number
  } | null>(null)

  // Parse le CSV localement dès qu'un fichier est choisi, pour la preview.
  useEffect(() => {
    if (!csvFile) {
      setCsvPreview(null)
      return
    }
    let cancelled = false
    parseProspectsCsv(csvFile)
      .then((res) => {
        if (!cancelled) setCsvPreview(res)
      })
      .catch(() => {
        if (!cancelled) setCsvPreview(null)
      })
    return () => {
      cancelled = true
    }
  }, [csvFile])

  const { data: existingCampaign, isLoading: loadingCampaign } = useCampaignDetail(
    campaignId ?? null,
  )
  const { data: ownedNumbers = [] } = useOwnedPDNumbers()
  const { data: teamMembers = [] } = useTeamMembers()

  const createCampaign = useCreateCampaign()

  // PATCH générique en mode édition (pas besoin d'un hook par id).
  const updateCampaign = useMutation({
    mutationFn: async ({ id, patch }: { id: number; patch: FormValues }) => {
      await api.patch(`/api/power-dialer/campaigns/${id}/`, patch)
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['power-dialer-campaigns'] })
      queryClient.invalidateQueries({
        queryKey: ['power-dialer-campaign', vars.id],
      })
    },
  })

  // useImportProspects nécessite un campaignId — instancié uniquement quand on a un id.
  const importProspects = useImportProspects(campaignId ?? 0)

  const form = useForm<FormValues>({
    resolver: zodResolver(campaignSchema),
    defaultValues: DEFAULTS,
  })
  const { control, register, setValue, handleSubmit, reset, formState } = form

  // Re-hydrate en mode édition quand le backend renvoie les données.
  useEffect(() => {
    if (existingCampaign) {
      reset({
        name: existingCampaign.name || '',
        description: existingCampaign.description || '',
        script: existingCampaign.script || '',
        number_ids: (
          (existingCampaign as unknown as { campaign_numbers?: { phone_number: number }[] })
            .campaign_numbers ?? []
        ).map((cn) => cn.phone_number),
        agent_ids: (
          (existingCampaign as unknown as { campaign_agents?: { agent: number }[] })
            .campaign_agents ?? []
        ).map((ca) => ca.agent),
        max_attempts: existingCampaign.max_attempts || 6,
        daily_cap_per_number:
          (existingCampaign as unknown as { daily_cap_per_number?: number })
            .daily_cap_per_number ?? 100,
        auto_rotation:
          (existingCampaign as unknown as { auto_rotation?: boolean }).auto_rotation ??
          true,
        local_presence_enabled:
          (existingCampaign as unknown as { local_presence_enabled?: boolean })
            .local_presence_enabled ?? false,
        sticky_enabled:
          (existingCampaign as unknown as { sticky_enabled?: boolean })
            .sticky_enabled ?? true,
      })
    }
  }, [existingCampaign, reset])

  const numberIds = useWatch({ control, name: 'number_ids' })
  const agentIds = useWatch({ control, name: 'agent_ids' })
  const autoRotation = useWatch({ control, name: 'auto_rotation' })
  const localPresenceEnabled = useWatch({ control, name: 'local_presence_enabled' })
  const stickyEnabled = useWatch({ control, name: 'sticky_enabled' })

  const toggleNumberId = (id: number) => {
    setValue(
      'number_ids',
      numberIds.includes(id) ? numberIds.filter((n) => n !== id) : [...numberIds, id],
      { shouldDirty: true },
    )
  }

  const toggleAgentId = (id: number) => {
    setValue(
      'agent_ids',
      agentIds.includes(id) ? agentIds.filter((n) => n !== id) : [...agentIds, id],
      { shouldDirty: true },
    )
  }

  const onSubmit = async (values: FormValues) => {
    try {
      let targetId = campaignId
      if (isEdit && campaignId) {
        await updateCampaign.mutateAsync({ id: campaignId, patch: values })
      } else {
        const created = await createCampaign.mutateAsync(values)
        targetId = created.id
      }
      if (csvFile && targetId) {
        // En création, le hook a été instancié avec id=0 — on patch via une mutation inline.
        if (!isEdit || campaignId !== targetId) {
          const formData = new FormData()
          formData.append('file', csvFile)
          const { data } = await api.post<{
            imported: number
            skipped: number
          }>(`/api/power-dialer/campaigns/${targetId}/import-csv/`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
          setImportResult(data)
        } else {
          const result = await importProspects.mutateAsync(csvFile)
          setImportResult(result)
        }
      }
      toast.success(
        t(
          isEdit
            ? 'manager.powerDialer.form.toast.updated'
            : 'manager.powerDialer.form.toast.created',
        ),
      )
      onBack()
    } catch {
      toast.error(t('manager.powerDialer.form.toast.error'))
    }
  }

  const errMsg = (key?: string) =>
    key ? t(`manager.powerDialer.errors.${key}`) : null

  const stepLabels: Record<StepKey, string> = {
    general: t('manager.powerDialer.form.steps.general'),
    settings: t('manager.powerDialer.form.steps.settings'),
    agents: t('manager.powerDialer.form.steps.agents'),
    prospects: t('manager.powerDialer.form.steps.prospects'),
  }

  const isSubmitting =
    formState.isSubmitting ||
    createCampaign.isPending ||
    updateCampaign.isPending ||
    importProspects.isPending

  if (loadingCampaign) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    )
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-slate-900 dark:text-white">
          {isEdit
            ? t('manager.powerDialer.form.editTitle')
            : t('manager.powerDialer.form.createTitle')}
        </h2>
        <span className="text-sm text-slate-400">
          {t('manager.powerDialer.form.stepCounter', {
            current: step + 1,
            total: STEPS.length,
          })}
        </span>
      </div>

      {/* Steps indicator */}
      <div className="flex gap-2">
        {STEPS.map((s, i) => (
          <button
            key={s}
            type="button"
            onClick={() => setStep(i)}
            className={cn(
              'flex-1 py-2 text-sm font-medium rounded-lg border transition-colors',
              i === step
                ? 'bg-blue-600 text-white border-blue-600'
                : i < step
                  ? 'bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-500/30'
                  : 'bg-white dark:bg-slate-900 text-slate-400 border-slate-200 dark:border-slate-700',
            )}
          >
            {i < step ? '✓ ' : ''}
            {stepLabels[s]}
          </button>
        ))}
      </div>

      {/* Step content */}
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6">
        {step === 0 && (
          <div className="space-y-4">
            <div>
              <Label htmlFor="campaign-name" className="text-sm font-medium">
                {t('manager.powerDialer.form.fields.name')}{' '}
                <span className="text-red-500">*</span>
              </Label>
              <Input
                id="campaign-name"
                placeholder={t('manager.powerDialer.form.fields.namePlaceholder')}
                className="mt-1"
                {...register('name')}
              />
              {formState.errors.name && (
                <p className="text-xs text-red-500 mt-1">
                  {errMsg(formState.errors.name.message)}
                </p>
              )}
            </div>
            <div>
              <Label htmlFor="campaign-description" className="text-sm font-medium">
                {t('manager.powerDialer.form.fields.description')}
              </Label>
              <textarea
                id="campaign-description"
                rows={3}
                placeholder={t('manager.powerDialer.form.fields.descriptionPlaceholder')}
                className="mt-1 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 resize-y"
                {...register('description')}
              />
            </div>
            <div>
              <Label htmlFor="campaign-script" className="text-sm font-medium">
                {t('manager.powerDialer.form.fields.script')}
              </Label>
              <textarea
                id="campaign-script"
                rows={6}
                placeholder={t('manager.powerDialer.form.fields.scriptPlaceholder')}
                className="mt-1 w-full rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-500 resize-y"
                {...register('script')}
              />
              <p className="text-xs text-slate-400 mt-1">
                {t('manager.powerDialer.form.fields.scriptHelp')}
              </p>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            <div>
              <Label className="text-sm font-medium mb-3 block">
                {t('manager.powerDialer.form.fields.outboundNumbers')}
              </Label>
              {ownedNumbers.length === 0 ? (
                <p className="text-sm text-slate-400">
                  {t('manager.powerDialer.form.fields.noNumbers')}
                </p>
              ) : (
                <div className="space-y-2">
                  {ownedNumbers.map((num) => (
                    <label
                      key={num.id}
                      className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={numberIds.includes(num.id)}
                        onChange={() => toggleNumberId(num.id)}
                        className="rounded border-slate-300 dark:border-slate-600"
                      />
                      <span className="font-mono text-sm text-slate-900 dark:text-white">
                        {num.phone_number}
                      </span>
                      {num.label && (
                        <span className="text-xs text-slate-400">{num.label}</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between">
              <Label className="text-sm font-medium">
                {t('manager.powerDialer.form.fields.autoRotation')}
              </Label>
              <Switch
                checked={autoRotation}
                onCheckedChange={(checked) =>
                  setValue('auto_rotation', checked, { shouldDirty: true })
                }
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex-1 min-w-0">
                <Label className="text-sm font-medium block">
                  {t('manager.powerDialer.form.fields.localPresence')}
                </Label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t('manager.powerDialer.form.fields.localPresenceHint')}
                </p>
              </div>
              <Switch
                checked={localPresenceEnabled}
                onCheckedChange={(checked) =>
                  setValue('local_presence_enabled', checked, { shouldDirty: true })
                }
              />
            </div>

            <div className="flex items-start justify-between gap-4 rounded-lg border border-slate-200 dark:border-slate-700 p-3">
              <div className="flex-1 min-w-0">
                <Label className="text-sm font-medium block">
                  {t('manager.powerDialer.form.fields.stickyCallerId')}
                </Label>
                <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                  {t('manager.powerDialer.form.fields.stickyCallerIdHint')}
                </p>
              </div>
              <Switch
                checked={stickyEnabled}
                onCheckedChange={(checked) =>
                  setValue('sticky_enabled', checked, { shouldDirty: true })
                }
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="daily-cap" className="text-sm font-medium">
                  {t('manager.powerDialer.form.fields.dailyCap')}
                </Label>
                <Input
                  id="daily-cap"
                  type="number"
                  min={1}
                  className="mt-1"
                  {...register('daily_cap_per_number', { valueAsNumber: true })}
                />
              </div>
              <div>
                <Label htmlFor="max-attempts" className="text-sm font-medium">
                  {t('manager.powerDialer.form.fields.maxAttempts')}
                </Label>
                <Input
                  id="max-attempts"
                  type="number"
                  min={1}
                  max={20}
                  className="mt-1"
                  {...register('max_attempts', { valueAsNumber: true })}
                />
              </div>
            </div>

            <CampaignDispositionsSection campaignId={campaignId ?? null} />
          </div>
        )}

        {step === 2 && (
          <div>
            <Label className="text-sm font-medium mb-3 block">
              {t('manager.powerDialer.form.fields.assignAgents')}
            </Label>
            {teamMembers.length === 0 ? (
              <p className="text-sm text-slate-400">
                {t('manager.powerDialer.form.fields.loadingAgents')}
              </p>
            ) : (
              <div className="space-y-2">
                {teamMembers.map((agent) => (
                  <label
                    key={agent.id}
                    className="flex items-center gap-3 p-3 border border-slate-200 dark:border-slate-700 rounded-lg cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={agentIds.includes(agent.id)}
                      onChange={() => toggleAgentId(agent.id)}
                      className="rounded border-slate-300 dark:border-slate-600"
                    />
                    <span className="text-sm font-medium text-slate-900 dark:text-white">
                      {agent.user_first_name} {agent.user_last_name}
                    </span>
                    <span className="text-xs text-slate-400">{agent.user_email}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 3 && (
          <div>
            <div className="flex items-center justify-between mb-3 gap-3">
              <Label className="text-sm font-medium">
                {t('manager.powerDialer.form.fields.importCsv')}
              </Label>
              <button
                type="button"
                onClick={() => downloadProspectsTemplate()}
                className="inline-flex items-center gap-1.5 text-xs font-medium text-[#E85D3A] hover:underline shrink-0"
              >
                <Download className="w-3.5 h-3.5" />
                {t('manager.powerDialer.form.fields.csvTemplate', 'Télécharger un modèle')}
              </button>
            </div>
            <label
              htmlFor="csv-input"
              className="block border-2 border-dashed border-slate-200 dark:border-slate-700 rounded-lg p-8 text-center hover:border-slate-400 dark:hover:border-slate-500 transition-colors cursor-pointer"
            >
              <input
                id="csv-input"
                type="file"
                accept=".csv"
                className="hidden"
                onChange={(e) => setCsvFile(e.target.files?.[0] ?? null)}
              />
              {csvFile ? (
                <div>
                  <p className="font-medium text-slate-900 dark:text-white">
                    {csvFile.name}
                  </p>
                  <p className="text-sm text-slate-400">
                    {(csvFile.size / 1024).toFixed(1)} KB
                  </p>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault()
                      setCsvFile(null)
                    }}
                    className="text-red-500 text-sm mt-2"
                  >
                    {t('manager.powerDialer.form.fields.removeFile')}
                  </button>
                </div>
              ) : (
                <div>
                  <Upload className="w-8 h-8 text-slate-300 dark:text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500 dark:text-slate-400">
                    {t('manager.powerDialer.form.fields.csvDropzone')}
                  </p>
                  <p className="text-xs text-slate-400 mt-1">
                    {t('manager.powerDialer.form.fields.csvColumns')}
                  </p>
                </div>
              )}
            </label>

            {/* Prévisualisation des numéros qui seront appelés */}
            {csvFile && csvPreview && (
              <div className="mt-4 border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden">
                {!csvPreview.phoneColumnFound ? (
                  <p className="p-4 text-sm text-red-500">
                    {t(
                      'manager.powerDialer.form.fields.csvNoPhoneColumn',
                      "Aucune colonne de téléphone détectée. Utilisez le modèle (colonne « telephone »).",
                    )}
                  </p>
                ) : (
                  <>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2.5 bg-slate-50 dark:bg-slate-800/50 text-xs">
                      <span className="font-semibold text-emerald-600 dark:text-emerald-400">
                        {t(
                          'manager.powerDialer.form.fields.csvValidCount',
                          '{{count}} prospect(s) à appeler',
                          { count: csvPreview.prospects.length },
                        )}
                      </span>
                      {(csvPreview.skippedNoPhone > 0 ||
                        csvPreview.duplicates > 0) && (
                        <span className="text-slate-400">
                          {t(
                            'manager.powerDialer.form.fields.csvSkipped',
                            '{{invalid}} ignoré(s), {{dup}} doublon(s)',
                            {
                              invalid: csvPreview.skippedNoPhone,
                              dup: csvPreview.duplicates,
                            },
                          )}
                        </span>
                      )}
                    </div>
                    <div className="max-h-56 overflow-y-auto divide-y divide-slate-100 dark:divide-slate-800">
                      {csvPreview.prospects.slice(0, 50).map((p, i) => (
                        <div
                          key={`${p.phone}-${i}`}
                          className="flex items-center gap-3 px-4 py-2"
                        >
                          <Phone className="w-3.5 h-3.5 text-slate-300 dark:text-slate-600 shrink-0" />
                          <span className="text-sm font-mono text-slate-700 dark:text-slate-200 shrink-0">
                            {p.phone}
                          </span>
                          <span className="text-sm text-slate-500 dark:text-slate-400 truncate">
                            {prospectDisplayName(p) === p.phone
                              ? ''
                              : prospectDisplayName(p)}
                            {p.company_name &&
                              `${`${p.first_name} ${p.last_name}`.trim() ? ' · ' : ''}${p.company_name}`}
                          </span>
                        </div>
                      ))}
                      {csvPreview.prospects.length > 50 && (
                        <div className="px-4 py-2 text-xs text-slate-400">
                          {t(
                            'manager.powerDialer.form.fields.csvMore',
                            '+ {{count}} autres…',
                            { count: csvPreview.prospects.length - 50 },
                          )}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
            {importResult && (
              <p className="text-sm text-emerald-600 dark:text-emerald-400 mt-3">
                {t('manager.powerDialer.form.fields.importResult', importResult)}
              </p>
            )}
          </div>
        )}
      </div>

      {/* Footer actions */}
      <div className="flex justify-between">
        <Button
          type="button"
          variant="ghost"
          onClick={step === 0 ? onBack : () => setStep(step - 1)}
        >
          {step === 0 ? (
            t('manager.powerDialer.form.cancel')
          ) : (
            <>
              <ArrowLeft className="h-4 w-4 mr-2" />
              {t('manager.powerDialer.form.previous')}
            </>
          )}
        </Button>
        {step < STEPS.length - 1 ? (
          <Button
            type="button"
            onClick={() => setStep(step + 1)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {t('manager.powerDialer.form.next')}
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button
            type="submit"
            disabled={isSubmitting}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEdit
              ? t('manager.powerDialer.form.save')
              : t('manager.powerDialer.form.create')}
          </Button>
        )}
      </div>
    </form>
  )
}
